// Utility functions for Google Fonts API
// Font constants are now in constants/fonts.js

import { CURATED_FONTS, CURATED_FONTS_CATEGORIZED, getFontVariants } from 'constants/fonts';

/**
 * Fetch curated fonts list
 * @returns {Promise<Array>} Array of font objects with 'family', 'category', and 'variants'
 */
export const fetchCuratedFonts = async () => {
    // Return the flattened curated fonts array
    return Promise.resolve(CURATED_FONTS);
};

/**
 * Get categorized fonts structure
 * @returns {Object} Categorized fonts object
 */
export const getCategorizedFonts = () => {
    return CURATED_FONTS_CATEGORIZED;
};

// Re-export getFontVariants for backward compatibility
export { getFontVariants };

/**
 * Load a Google Font dynamically
 * @param {string} fontFamily - The font family name
 * @param {Array<string>} variants - Array of font weights to load (e.g., ['400', '600']). If not provided, will use variants from curated list.
 * @returns {Promise<void>}
 */
export const loadGoogleFont = (fontFamily, variants = null) => {
    // If variants not provided, get them from curated fonts list
    if (!variants) {
        variants = getFontVariants(fontFamily);
    }
    return new Promise((resolve, reject) => {
        // Map system fonts to Google Fonts equivalents
        let fontToLoad = fontFamily;
        if (fontFamily === 'Garamond') {
            fontToLoad = 'EB Garamond';
        } else if (fontFamily === 'Baskerville') {
            fontToLoad = 'Libre Baskerville';
        }

        // Check if font is already loaded
        const fontId = `google-font-${fontToLoad.replace(/\s+/g, '-').toLowerCase()}`;
        const existingLink = document.getElementById(fontId);

        if (existingLink) {
            // Font link exists, check if font is actually loaded
            if (document.fonts && document.fonts.check) {
                const fontString = `400 16px "${fontToLoad}"`;
                if (document.fonts.check(fontString)) {
                    resolve();
                    return;
                }
            } else {
                // Fallback: assume loaded if link exists
                resolve();
                return;
            }
        }

        // Build the font URL with display=swap for immediate rendering
        const weights = variants.join(',');
        const fontName = fontToLoad.replace(/\s+/g, '+');
        const url = `https://fonts.googleapis.com/css2?family=${fontName}:wght@${weights}&display=swap`;

        // Create and append link element
        const link = document.createElement('link');
        link.id = fontId;
        link.rel = 'stylesheet';
        link.href = url;

        // Wait for font to actually load
        const checkFontLoaded = () => {
            // Give the browser time to process the font
            setTimeout(() => {
                if (document.fonts && document.fonts.check) {
                    const fontString = `400 16px "${fontToLoad}"`;
                    // Check multiple times with increasing delays
                    let attempts = 0;
                    const maxAttempts = 10;
                    const checkInterval = setInterval(() => {
                        attempts++;
                        if (document.fonts.check(fontString) || attempts >= maxAttempts) {
                            clearInterval(checkInterval);
                            resolve();
                        }
                    }, 100);
                } else {
                    // Fallback: resolve after a short delay
                    setTimeout(() => resolve(), 200);
                }
            }, 100);
        };

        link.onload = checkFontLoaded;
        link.onerror = () => {
            console.warn(`Failed to load font from Google Fonts: ${fontToLoad}, trying fallback`);
            // Even if loading fails, resolve so the component can render with fallback
            resolve();
        };
        document.head.appendChild(link);
    });
};

/**
 * Preload multiple fonts
 * @param {Array<string>} fontFamilies - Array of font family names
 * @returns {Promise<void>}
 */
export const preloadFonts = async (fontFamilies) => {
    const loadPromises = fontFamilies.map(font =>
        loadGoogleFont(font, ['400', '500', '600', '700']).catch(err => {
            console.warn(`Failed to preload font ${font}:`, err);
        })
    );
    await Promise.all(loadPromises);
};

/**
 * Get font category label
 * @param {string} category - Font category (e.g., 'sans-serif', 'serif')
 * @returns {string} Display label
 */
export const getFontCategoryLabel = (category) => {
    const labels = {
        'sans-serif': 'Sans Serif',
        'serif': 'Serif',
        'monospace': 'Monospace',
        'display': 'Display',
        'handwriting': 'Handwriting'
    };
    return labels[category] || category;
};

